/**
 * cardTurnIn — pure helpers for the forced card-turn-in prompt.
 *
 * `state.mustTurnInCards` is a SINGLE GLOBAL flag that tracks whoever is the
 * current player. The engine sets it true when the current player enters
 * placement holding ≥5 cards, or is forced to trade in after an elimination.
 *
 * During bot turns the WS GAME_STATE_PATCH events carry the BOT's value of
 * this flag. A human client must therefore NOT auto-open its turn-in modal
 * purely because `mustTurnInCards` is true — that flag may belong to a bot
 * mid-turn, in which case opening the modal would (a) show the human their
 * own, possibly setless / sub-5 hand, and (b) obscure the bot's moves behind
 * a modal. Gate on the viewer actually being the current player.
 */

/** Minimal projection of the wire GameState this helper needs. */
export interface TurnInStateProjection {
  mustTurnInCards?: boolean
  /** Engine player list — slot ids as strings, in turn order. */
  players?: string[]
  /** Index into `players` of the current player. */
  turnIndex?: number
  /** Per-player hands keyed by slot-id string — used to verify the ≥5 cap. */
  cards?: Record<string, unknown[]>
}

/** Cards a player must hold before the forced turn-in applies (Risk's 5-cap). */
export const FORCED_TURN_IN_THRESHOLD = 5

/**
 * Whether a hand of this size FORCES a turn-in — derived purely from card
 * count, NOT the persisted `mustTurnInCards` flag. Drives the HUD card
 * button's blood-red "must trade in" pulse so a stale/bled flag can never
 * make it flash on a hand that doesn't actually owe a turn-in.
 */
export function handForcesTurnIn(handCount: number): boolean {
  return handCount >= FORCED_TURN_IN_THRESHOLD
}

/**
 * True iff the engine requires THIS viewer to trade in cards right now —
 * i.e. the forced-turn-in flag is set, the viewer is the current player, and
 * the viewer actually holds ≥5 cards.
 *
 * The hand-size check is deliberately NOT trusted to the engine flag alone.
 * `mustTurnInCards` is a single GLOBAL flag, and bugs (e.g. a resigning
 * player's flag bleeding onto the next player — fixed in engine/resign.ts)
 * or a stale WS patch can leave it true for a viewer holding <5 cards. Without
 * the local guard that soft-locks the player into a forced modal they can't
 * satisfy, so we re-verify the viewer's hand here.
 *
 * This predicate answers only "does the viewer OWE a forced turn-in?" — it does
 * NOT decide when the modal opens. Sequencing (banner → free-fortify → turn-in →
 * placement) lives in lib/eliminationFlow.ts, which consumes this result.
 */
export function viewerMustTurnIn(
  state: TurnInStateProjection | undefined,
  viewerSlotId: number | undefined,
): boolean {
  if (!state?.mustTurnInCards) return false
  if (viewerSlotId === undefined) return false
  if (!state.players || state.turnIndex === undefined) return false
  if (state.players[state.turnIndex] !== String(viewerSlotId)) return false
  // Re-verify the viewer genuinely owes a forced turn-in (≥5 cards). Guards
  // against a stale/bled global flag soft-locking a sub-5 hand.
  const hand = state.cards?.[String(viewerSlotId)] ?? []
  return hand.length >= FORCED_TURN_IN_THRESHOLD
}
